import { ReadDefensorResponseDto, ReadLotacaoDto } from '@/api.types';
import { TipoLotacao } from '@/modules/business/lotacoes/enums/tipo-lotacao.enum';
import type { DefensoriaOpcao, LotacaoTitular } from './relatorios-atividades.types';

export function parseDateOnly(input: string | Date | null | undefined): Date | null {
  if (!input) return null;
  if (input instanceof Date) {
    if (isNaN(input.getTime())) return null;
    return new Date(input.getFullYear(), input.getMonth(), input.getDate());
  }

  const raw = String(input).trim();
  if (!raw) return null;

  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10) - 1;
    const d = parseInt(m[3], 10);
    return new Date(y, mo, d);
  }

  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function LotacaoVigenteNoMes(lotacao: any, mes: string, ano: number): boolean {
  if (!lotacao) return false;

  const mesNumero = parseInt(mes, 10);
  if (Number.isNaN(mesNumero)) return false;

  const inicioMes = new Date(ano, mesNumero - 1, 1);
  const fimMes = new Date(ano, mesNumero, 0);

  if (lotacao.ativo === false) return false;

  const dataInicial = parseDateOnly(lotacao.data_inicial);
  const dataFinal = parseDateOnly(lotacao.data_final);

  const inicio = dataInicial ?? new Date(1900, 0, 1);
  const fim = dataFinal ?? new Date(2100, 0, 1);

  return inicio <= fimMes && fim >= inicioMes;
}

export function ExtrairDefensoriasOpcoes(lotacoes: ReadLotacaoDto[]): DefensoriaOpcao[] {
  const defensoriaMap = new Map<number, DefensoriaOpcao>();

  lotacoes.forEach((lotacao: any) => {
    const defensoriaHabilitada =
      lotacao.defensoria && (lotacao.defensoria as any).relatoriosAtividadesHabilitado === true;

    if (defensoriaHabilitada && lotacao.defensoria.id && !defensoriaMap.has(lotacao.defensoria.id)) {
      defensoriaMap.set(lotacao.defensoria.id, {
        id: lotacao.defensoria.id,
        nome: lotacao.defensoria.nome || 'Sem defensoria',
        comarca: lotacao.defensoria.comarca?.nome || 'Sem comarca',
        criminal: lotacao.defensoria.criminal ?? false,
        civel: lotacao.defensoria.civel ?? false,
      });
    }
    if (!lotacao.defensoria && lotacao.nucleo && lotacao.nucleo.id && !defensoriaMap.has(lotacao.nucleo.id)) {
      defensoriaMap.set(lotacao.nucleo.id, {
        id: lotacao.nucleo.id,
        nome: lotacao.nucleo.nome || 'Sem núcleo',
        comarca: 'Núcleo',
        criminal: false,
        civel: false,
      });
    }
  });

  return Array.from(defensoriaMap.values());
}

export function ExtrairDefensoriasOpcoesPorMes(
  lotacoes: ReadLotacaoDto[],
  mes: string,
  ano: number,
): DefensoriaOpcao[] {
  if (!mes || !ano) {
    return ExtrairDefensoriasOpcoes(lotacoes);
  }

  const lotacoesNoMes = lotacoes.filter((l: any) => LotacaoVigenteNoMes(l, mes, ano));
  return ExtrairDefensoriasOpcoes(lotacoesNoMes);
}

export function ObterComarcaVara(_defensor: ReadDefensorResponseDto | null, lotacoes: ReadLotacaoDto[]) {
  const lotacaoComDefensoria = lotacoes.find((lotacao: any) => lotacao.defensoria);

  if (lotacaoComDefensoria) {
    const defensoria = (lotacaoComDefensoria as any).defensoria;
    return {
      comarca: defensoria?.comarca?.nome || 'Sem comarca',
      vara: defensoria?.vara?.nome || defensoria?.nome || 'Sem vara',
    };
  }

  const lotacaoComNucleo = lotacoes.find((lotacao: any) => lotacao.nucleo);
  if (lotacaoComNucleo) {
    return {
      comarca: 'Núcleo',
      vara: (lotacaoComNucleo as any).nucleo?.nome || 'Sem núcleo',
    };
  }

  return {
    comarca: 'Sem comarca',
    vara: 'Sem lotação',
  };
}

/**
 * Precedência usada quando o defensor possui mais de uma lotação vigente
 * na mesma defensoria/núcleo no período do relatório.
 */
const PRECEDENCIA_TIPO_LOTACAO: TipoLotacao[] = [
  TipoLotacao.Titularidade,
  TipoLotacao.Designacao_Principal,
  TipoLotacao.Designacao_Especial,
  TipoLotacao.Designacao,
  TipoLotacao.Acumulacao,
  TipoLotacao.Substituicao,
];

/**
 * Tipo de lotação do defensor na defensoria/núcleo do relatório, considerando o mês/ano de referência.
 * Sem `defensoriaId`, avalia todas as lotações vigentes no período.
 * Retorna `null` quando não há lotação de defensor aplicável (inclui lotações de estágio).
 */
export function ObterTipoLotacaoDoRelatorio(
  lotacoes: ReadLotacaoDto[] | undefined,
  defensoriaId: number | null | undefined,
  mes: string | number,
  ano: number,
): TipoLotacao | null {
  if (!lotacoes || lotacoes.length === 0) return null;

  const vigentes = lotacoes.filter(
    (lotacao) => lotacao.ativo !== false && LotacaoVigenteNoMes(lotacao, String(mes), ano),
  );

  const doLocalDoRelatorio = defensoriaId
    ? vigentes.filter(
      (lotacao) => lotacao.defensoria?.id === defensoriaId || lotacao.nucleo?.id === defensoriaId,
    )
    : vigentes;

  if (doLocalDoRelatorio.length === 0) return null;

  return (
    PRECEDENCIA_TIPO_LOTACAO.find((tipo) => doLocalDoRelatorio.some((lotacao) => lotacao.tipo === tipo)) ?? null
  );
}

export function ObterLotacaoTitular(lotacoes?: ReadLotacaoDto[]): LotacaoTitular | null {
  if (!lotacoes || lotacoes.length === 0) {
    return null;
  }

  const hoje = new Date();

  const lotacaoTitular = lotacoes.find((lotacao: any) => {
    const dataFinal = lotacao.data_final ? new Date(lotacao.data_final) : null;
    const estaVigente = !dataFinal || dataFinal >= hoje;
    const tipo = lotacao.tipo;
    const eTitular = tipo === TipoLotacao.Titularidade;

    return estaVigente && lotacao.ativo && eTitular;
  });

  if (!lotacaoTitular) {
    return null;
  }

  const tipoNome = 'Titularidade';
  const tipoAbreviado = '[T]';

  const nome =
    (lotacaoTitular as any).defensoria?.nome ||
    (lotacaoTitular as any).nucleo?.nome ||
    'Não informado';

  return {
    tipo: tipoNome,
    nome,
    tipoAbreviado,
  };
}
